import { useState } from "react";
import {
  SkyBody1,
  SkyButton,
  SkyDialog,
  SkyInput,
  SkyTextarea,
  SkyDropdown,
  SkyDivider,
} from "@skypointcloud/skypoint-ui";
import { FileText } from "lucide-react";

import { getInstanceIdFromUrl } from "../../../utils/instanceId";
import { useNotification } from "../../../common/hooks/useNotification";
import {
  useCreateDraftMutation,
  useGetDraftsQuery,
  type DraftKind,
} from "../../../store/api/crmAgentPinsDraftsApi";

/**
 * Keep something the agent wrote, so it survives the chat scrolling away.
 *
 * ## THE ONE THING THIS COMPONENT MUST NEVER BE AMBIGUOUS ABOUT
 *
 * A draft has been WRITTEN and NOT SENT. The harm to avoid is specific: a
 * counselor believing a family was emailed when nobody emailed them.
 *
 * So the word "send" does not appear on this surface, the button says Save rather
 * than anything that could read as dispatch, and the dialog states the limit in
 * plain words before the person commits. That is on top of the structural
 * guarantee — there is no send endpoint behind any of this, and drafts live in
 * their own table that no timeline reads.
 *
 * ## Why the kind matters
 *
 * "Email" is the dangerous one: a saved thing labelled Email is exactly what
 * somebody would assume had gone out. It is offered because it is what the agent
 * was asked to write, and the label beside it does the work — every rendering of
 * a draft in this app says it is unsent.
 */

const KIND_OPTIONS: Array<{ value: DraftKind; text: string }> = [
  { value: "email", text: "Email" },
  { value: "message", text: "Text message" },
  { value: "note", text: "Note" },
  { value: "document", text: "Document" },
];

/** A first line that reads like a title, for the default name. */
function suggestTitle(body: string): string {
  const firstLine = body
    .split("\n")
    .map((l) => l.replace(/^#+\s*/, "").trim())
    .find((l) => l.length > 0);
  if (!firstLine) return "Untitled draft";
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : firstLine;
}

const SaveDraftButton = ({
  body,
  sessionId,
}: {
  /** The agent's answer, as rendered. */
  body: string;
  sessionId: string | null;
}) => {
  const instanceId = getInstanceIdFromUrl() || "";
  const { addNotification } = useNotification();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<DraftKind>("message");
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Only used to learn whether the capability is on. `limit: 1` so the probe
  // costs a single row rather than a page nobody renders.
  const { data } = useGetDraftsQuery({ instanceId, limit: 1 }, { skip: !instanceId });
  const [createDraft, { isLoading }] = useCreateDraftMutation();

  // Absence is the gate: no button at all, rather than one that explains itself.
  if (data?.enabled === false) return null;

  const openDialog = () => {
    setTitle(suggestTitle(body));
    setText(body);
    setKind("message");
    setError(null);
    setOpen(true);
  };

  const save = async (): Promise<boolean> => {
    const name = title.trim();
    const content = text.trim();
    if (!name || !content) {
      setError("Give it a name, and leave something to save.");
      return false;
    }
    try {
      await createDraft({ instanceId, kind, title: name, body: content, sessionId }).unwrap();
      addNotification({ type: "SUCCESS", message: "Saved to your drafts. Nothing has been sent." });
      setOpen(false);
      return true;
    } catch (err) {
      // Inline. The save failed and the person is still holding the text.
      setError(
        (err as { data?: { error?: string } })?.data?.error ?? "Could not save that. Try again.",
      );
      return false;
    }
  };

  return (
    <>
      <SkyButton
        appearance="subtle"
        size="small"
        icon={FileText}
        aria-label="Save this as a draft"
        title="Save this as a draft"
        onClick={openDialog}
      />

      <SkyDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setError(null);
          }
        }}
        title="Save as a draft"
        // The dialog closes itself on the primary action, so the submit runs from
        // here — otherwise a failed save would set its error into a dialog that
        // had already gone. Same fix as the connectors dialog.
        onBeforeClose={(reason) => (reason === "primaryAction" ? save() : true)}
        primaryAction={{ label: isLoading ? "Saving…" : "Save draft", onClick: () => {} }}
        secondaryAction={{ label: "Cancel", onClick: () => setOpen(false) }}
      >
        <div className="flex flex-col gap-3">
          {/* Said BEFORE the fields, not after. This is the sentence that stops
              someone closing the dialog believing a family has been contacted. */}
          <SkyBody1
            className="block font-medium"
            data-testid="draft-not-sent-notice"
          >
            This saves the text. It does not send anything to anyone.
          </SkyBody1>

          <SkyInput
            label="Name"
            value={title}
            maxLength={200}
            onChange={(e) => {
              setTitle(e.target.value);
              setError(null);
            }}
          />

          <SkyDropdown
            label="What is it"
            options={KIND_OPTIONS}
            selectedOptions={[kind]}
            onOptionSelect={(vals) => setKind((vals[0] as DraftKind) ?? kind)}
          />

          <SkyTextarea
            label="The draft"
            value={text}
            rows={10}
            maxLength={20000}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
          />

          {error && (
            <SkyBody1
              data-testid="draft-save-error"
              className="block text-destructive"
            >
              {error}
            </SkyBody1>
          )}

          <SkyDivider />

          <SkyBody1 className="block text-sm">
            You will find it under Drafts, and you can file it against a prospect there. Sending it
            is a separate step you take yourself, on the prospect&apos;s record.
          </SkyBody1>
        </div>
      </SkyDialog>
    </>
  );
};

export default SaveDraftButton;
